import {omitProperties, onlyOwnProperties} from '@/lib/ReusableFunctions/Object';
import {OverlayService} from '@/services/OverlaysService';
import {Markers} from '@/services/MapService';
import {getLinkedItemsForBoard} from '@/services/MondayService/linkedItems';
import {BoardNames} from '@/services/MondayService/config';
import {getCollection} from '@/lib/filters/filters';

// Classes
import {DisplayField} from '../DisplayField';
import {Link} from '@/lib/classes/Link';
import {InfoWindowContent} from '../InfoWindowContent';

const formatCompsForContent = (infoWindowContent, comps = []) => comps.map((comp) => {
  const displayFields = (comp.displayFields ?? []).map((field) => {
    const displayField = new DisplayField(field);

    if (displayField.unit === 'link') {
      displayField.value = new Link({
        url: displayField.value?.url ?? displayField.value,
        text: 'Click Here',
        type: comp.linkType ?? 'link',
      });
    }
    return displayField;
  });

  const link = comp.link ? new Link({
    url: comp.link,
    text: comp.linkText ?? 'View on Salesforce',
    type: comp.linkType ?? 'salesforce',
  }) : undefined;

  return {
    type: comp.type,
    id: comp.id,
    name: comp.name,
    link,
    parent: infoWindowContent,
    displayFields,
  };
});

const complianceBoards = [
  {boardName: BoardNames.BROWNFIELD_REQUIREMENTS, compName: 'Brownfield'},
  {boardName: BoardNames.BONDS_LETTER_CREDIT, compName: 'Bond/Letter of Credit'},
];

const toSlug = (value) => value.toLowerCase().replace(/\s+/g, '-');

const buildMondayComplianceComps = async (linkedDocuments = []) => {
  const comps = [];

  for (const {boardName, compName} of complianceBoards) {
    const linkedItem = await getLinkedItemsForBoard(boardName, linkedDocuments);
    if (!linkedItem?.displayFields?.length) continue;

    comps.push({
      type: 'Compliance Requirements',
      name: compName,
      id: `compliance-${toSlug(boardName)}`,
      link: linkedItem.boardUrl,
      linkText: 'View on Monday.com',
      linkType: 'link',
      displayFields: linkedItem.displayFields,
    });
  }

  return comps;
};

/**
 * @typedef {import('@/services/OverlaysService/Overlay').Overlay} Overlay
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').InfoWindowContentData} InfoWindowContentData
 */

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new info window content instance for NorthPoint industrial buildings
 * @param {Object} building a building object coming from the NorthPoint industrial queries
 * @param {{includeMondayComps?: boolean}} [options] Set `includeMondayComps` to false to skip Monday.com fetching so
 * exporters can avoid waiting on the download if desired.
 * @return {InfoWindowContent} the content that will be used in the infowindow for NorthPoint industrial buildings
 * ---------------------------------------------------------------------------------------------------------------------
 */
async function createIndustrialBuildingContent(building, options = {}) {
  try {
    const {includeMondayComps = true} = options;
    const displayFields = building.displayFields.map((field) => new DisplayField(field));

    // Make a copy of the building without the fields we don't want in metadata
    const omitProps = ['comps', 'displayFields'];
    const shallowBuildingCopy = omitProperties(onlyOwnProperties(building), ...omitProps);

    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'industrialBuilding',
      title: building.name,
      id: building.id,
      location: building.location,
      tabIcon: {name: building.markerData.icon},
      metadata: shallowBuildingCopy,
      displayFields,
      host: {
        setHighlight: (state) => Markers.setHighlight(building.markerData.collection, building.markerData.id, state),
        filterCollection: building.filterCollection,
        firestoreCollection: building.firestoreCollection,
      },
      linkedDocuments: building.linkedDocuments ?? [],
    };

    // Make a link using the building's link field
    if (building.link) {
      contentData.link = new Link({
        url: building.link,
        text: building.name,
        type: 'salesforce',
      });
    }

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);

    // Create comps after making infoWindowContent so that we can set the parent
    const formatComps = (comps = []) => formatCompsForContent(infoWindowContent, comps);
    infoWindowContent.setComps(formatComps(building.comps));

    if (!includeMondayComps) {
      return infoWindowContent;
    }

    const linkedDocuments = building.linkedDocuments ?? [];
    const mondayItemsCollection = getCollection('mondayItems');

    const upsertMondayComps = async () => {
      const mondayComps = await buildMondayComplianceComps(linkedDocuments);
      if (!mondayComps.length) return;

      const formattedMondayComps = formatComps(mondayComps);
      formattedMondayComps.forEach((comp) => {
        const existingIndex = infoWindowContent.comps.findIndex(({id}) => id === comp.id);
        if (existingIndex >= 0) {
          infoWindowContent.comps.splice(existingIndex, 1, comp);
        } else {
          infoWindowContent.addComp(comp);
        }
      });
    };

    if (mondayItemsCollection?.getSource().length) {
      await upsertMondayComps();
    } else if (mondayItemsCollection) {
      mondayItemsCollection.subscribe('add', () => {
        void upsertMondayComps();
      }, {once: true});
    }

    return infoWindowContent;
  } catch (error) {
    console.warn('Error creating industrial building content', error);
    return null;
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * createProjectContent - creates info window content for a given NorthPoint project
 * @param {Object} project - The project to make content for
 * @param {boolean} [fetchOverlays=true] - Whether to fetch overlays for the project
 * @return {InfoWindowContent} The content used for the info window
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createProjectContent(project, fetchOverlays = true) {
  const displayFields = project.displayFields.map((field) => {
    const displayField = new DisplayField(field);
    if (field.unit === 'link' && field.value) {
      displayField.value = new Link(displayField.value);
    }
    return displayField;
  });

  // Make a copy of the building without the fields we don't want in metadata
  const omitProps = ['comps', 'displayFields'];
  const shallowBuildingCopy = omitProperties(onlyOwnProperties(project), ...omitProps);

  /** @type {InfoWindowContentData} */
  const contentData = {
    type: project.type,
    title: project.name,
    id: project.id,
    location: project.location,
    metadata: shallowBuildingCopy,
    tabIcon: {name: project.markerData.icon},
    displayFields,
    host: {
      setHighlight: (state) => Markers.setHighlight(project.markerData.collection, project.markerData.id, state),
      filterCollection: project.filterCollection,
      firestoreCollection: project.firestoreCollection,
    },
  };

  if (project.link) {
    contentData.link = new Link({
      url: project.link,
      text: project.name,
      type: 'salesforce',
    });
  }

  // Make a link using the building's link field
  if (project.link) {
    contentData.link = new Link({
      url: project.link,
      text: project.name,
      type: 'salesforce',
    });
  }

  if (fetchOverlays) {
    // overlaysByParent returns a promise, we wait for it to resolve in NorthPointInfoWindow.vue
    contentData.overlays = OverlayService.overlaysByParent(project.id ?? project.projectNumber);
  }

  // Create info window content
  const infoWindowContent = new InfoWindowContent(contentData);

  // Format and set comps
  infoWindowContent.setComps(formatCompsForContent(infoWindowContent, project.comps));

  return infoWindowContent;
}

export {
  createIndustrialBuildingContent,
  createProjectContent,
};
